import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Client } from 'src/app/models/client.model';
import { User } from 'src/app/models/user.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { UserService } from 'src/app/services/user.service';
import { isWelbyClinicalValidator } from 'src/app/shared/validators/new-user.validators';
@Component({
  selector: 'app-provider-dialog',
  templateUrl: './provider-dialog.component.html',
  styleUrls: ['./provider-dialog.component.scss'],
})
export class ProviderDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  loading = false;
  serverMessage: string;
  states = ['AZ', 'CA', 'NV'];
  clients: Client[] = [];
  leads: User[] = [];
  highestLicensures: string[] = [];
  unsubscribe$ = new Subject<void>();
  /**
   * Only admins may invite a Welby Clinical user: the role is not scoped to the invited clinic (it can
   * remove any patient on the platform), so a practice admin would grant more than they hold. The
   * welby-firestore rule and the sign-up endpoint refuse it too; this just stops offering it.
   */
  canInviteWelbyClinical = false;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<ProviderDialogComponent>,
    public dataModel: InitialDataModel,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private auth: NewAuthService
  ) {}

  ngOnInit(): void {
    this.canInviteWelbyClinical = this.auth.user?.roles?.isAdmin === true;
    this.form = this.fb.group(
      {
        client: ['', [Validators.required]],
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        email: ['', [Validators.email, Validators.required]],
        isWelbyClinical: [false],
        lead: [''],
        highest_licensure: [''],
      },
      { validators: [isWelbyClinicalValidator] }
    );
    this.clients = Object.values(this.dataModel.clients);
    this.userService
      .getLeadNurses()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((leads) => {
        this.leads = leads;
      });
    this.userService
      .getProvidersRoles()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((roles) => {
        this.highestLicensures = roles;
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  async onSubmit(): Promise<void> {
    if (this.form.valid) {
      this.data.provider = this.form.value;
      this.data.provider.email = this.form.value.email?.toLowerCase().trim();
      this.data.provider.firstName = this.form.value.firstName?.trim();
      this.data.provider.lastName = this.form.value.lastName?.trim();
      this.data.provider.highest_licensure = this.form.value.highest_licensure;
      // Never sent as Welby Clinical by someone who may not grant it, whatever the form holds.
      this.data.provider.isWelbyClinical = this.canInviteWelbyClinical && this.form.value.isWelbyClinical === true;
      if (this.data.provider.isWelbyClinical && this.form.value.lead) {
        this.data.lead = {
          id: this.form.value.lead.user_id,
          firstName: this.form.value.lead.firstName,
          lastName: this.form.value.lead.lastName,
        };
      }
      this.dialogRef.close(this.data);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
